import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { useStyles } from './CommentsOutsidePageEditor.styles';
import ArrowCircleRightRoundedIcon from '@mui/icons-material/ArrowCircleRightRounded';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import ClearIcon from '@mui/icons-material/Clear';
import CancelIcon from '@material-ui/icons/Cancel';
import { Prompt } from '../../Prompt/Prompt.component';
import { CommentTypeId } from '../Comments.types';
import { addComment, deleteComment, updateComment } from '../../../services/exams/examTaskGroup/examTaskGroup.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../../Context/Layout.context';
import { useScoring } from '../../../Context/Scoring.context';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { ExamTaskGroupSolutionScoreShelfResponse } from '../../../models/assessment.types';
import { Status } from '../../../services/exams/assessmentSession/assessments.types';
import useSessionUser from '../../../hooks/useSessionUser';

interface CommentsOutsidePageEditor {
  commentProp: string;
  existingComment?: boolean;
  onSave: (options?: { scoreVersionId?: string; commentTypeId?: CommentTypeId }) => Promise<unknown>;
  isActionVisible: boolean;
  rgzId?: string;
  commentId: string;
  canAddComment?: boolean;
  isCommentOnCriterion?: boolean;
  canAddCommentOnCriterion?: boolean;
  setEditedItemId?: (id: string) => void;
  isEnabled?: boolean;
  canEditScore: boolean;
  canDeleteForcedComment: boolean;
}

const COMMENTS_MAX_LENGTH = 2000;
const COMMENTS_MIN_LENGTH = 32;

export const CommentsOutsidePageEditor: React.FC<CommentsOutsidePageEditor> = ({
  commentProp,
  existingComment,
  onSave,
  isActionVisible,
  rgzId,
  commentId,
  canAddComment,
  isCommentOnCriterion,
  canAddCommentOnCriterion,
  setEditedItemId,
  isEnabled,
  canEditScore,
  canDeleteForcedComment,
}) => {
  const classes = useStyles();
  const [comment, setComment] = useState<string>(commentProp ?? '');
  const [hasCommentFocus, setHasCommentFocus] = useState<boolean>(false);
  const [isPromptOpen, setIsPromptOpen] = useState<boolean>(false);
  const { openAlert, showLoader } = useLayoutContext();
  const { user, isUserKK, isUserKGZ, isUserCZE, isUserCZWO } = useSessionUser();

  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  const {
    activeCriterion,
    openScoreShelf,
    orgzId,
    examTaskGroupSolutionWithTasks,
    setIsCommentUpdate,
    examTaskGroupSolutionScoreVersionId,
    setExamTaskGroupSolutionScoreVersionId,
    setIsCommentEdit,
  } = useScoring();

  const isCommentToLong = comment.length > COMMENTS_MAX_LENGTH;
  const isCommentToShort = comment.length < COMMENTS_MIN_LENGTH;

  const getShelfPartialScoreIdForActiveCriterion = (
    responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
  ): string | undefined => {
    const activeSchemaId = activeCriterion?.examScoringSchemaId;
    if (!activeSchemaId) return undefined;

    return responseData?.examTasksCriteriaDto?.examTasksCriteria
      ?.flatMap((task) => task.examScoringSchemas)
      .find((schema) => schema.examScoringSchemaId === activeSchemaId)?.partialScoreId;
  };

  const getShelfOnCriteriaCommentId = (
    responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
    partialScoreId: string,
  ): string | undefined => {
    const currentUserId = user?.profile?.sub;
    if (!currentUserId) return undefined;

    return responseData?.commentsDto.find(
      (c) =>
        c.commentTypeId === CommentTypeId.ONCRITERIA &&
        c.partialScoreId === partialScoreId &&
        c.examinerId === currentUserId,
    )?.commentId;
  };

  const scoreStateAssessed =
    examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState &&
    examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState === Status.Closed;

  const syncAndResolveScoreVersionId = (
    responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
  ): string | undefined => {
    const resolvedVersionId = responseData?.examTaskGroupSolutionScoreVersionId ?? examTaskGroupSolutionScoreVersionId;
    if (responseData?.examTaskGroupSolutionScoreVersionId) {
      setExamTaskGroupSolutionScoreVersionId(responseData.examTaskGroupSolutionScoreVersionId);
    }
    return resolvedVersionId;
  };

  useEffect(() => {
    if (hasCommentFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [hasCommentFocus]);

  useEffect(() => {
    setComment(commentProp);
  }, [activeCriterion, commentProp]);

  const handleCommentChange = (value: string) => {
    if (isCommentOnCriterion && canAddCommentOnCriterion) {
      setIsCommentEdit(true);
    }
    setComment(value);
  };

  const handleAdd = () => {
    if (
      orgzId &&
      canEditScore &&
      scoreStateAssessed &&
      !examTaskGroupSolutionScoreVersionId &&
      isCommentOnCriterion &&
      canAddCommentOnCriterion &&
      (isUserKGZ || isUserKK)
    ) {
      showLoader(true, 'Zapisywanie danych');
      setIsCommentUpdate(true);
      openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
        const resolvedVersionId = syncAndResolveScoreVersionId(responseData);
        const newPartialScoreId = getShelfPartialScoreIdForActiveCriterion(responseData);
        if (!newPartialScoreId) {
          openAlert('error');
          setIsCommentEdit(false);
          setIsCommentUpdate(false);
          return;
        }
        return await addComment(
          rgzId,
          CommentTypeId.ONCRITERIA,
          comment,
          null,
          null,
          null,
          null,
          null,
          newPartialScoreId,
        )
          .then(
            (response: AxiosResponse<boolean>) => {
              if (response.data) {
                return onSave({ scoreVersionId: resolvedVersionId });
              } else {
                openAlert('error');
              }
            },
            () => {
              openAlert('error');
            },
          )
          .catch(() => {
            openAlert('error');
          })
          .finally(() => {
            setIsCommentEdit(false);
            setIsCommentUpdate(false);
          });
      });
      return;
    }

    showLoader(true, 'Zapisywanie danych');
    addComment(
      rgzId,
      isCommentOnCriterion && canAddCommentOnCriterion ? CommentTypeId.ONCRITERIA : CommentTypeId.OFFPAGE,
      comment,
      null,
      null,
      null,
      null,
      null,
      isCommentOnCriterion && canAddCommentOnCriterion && activeCriterion ? activeCriterion.partialScoreId : null,
    )
      .then(
        (response: AxiosResponse<boolean>) => {
          if (response.data) {
            onSave({
              commentTypeId:
                isCommentOnCriterion && canAddCommentOnCriterion ? CommentTypeId.ONCRITERIA : CommentTypeId.OFFPAGE,
            });
          } else {
            openAlert('error');
          }
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => {
        showLoader(false);
        setIsCommentEdit(false);
      });
  };

  const saveEdited = () => {
    if (
      orgzId &&
      canEditScore &&
      scoreStateAssessed &&
      !examTaskGroupSolutionScoreVersionId &&
      isCommentOnCriterion &&
      (isUserKGZ || isUserKK)
    ) {
      showLoader(true);
      setIsCommentUpdate(true);
      openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
        const resolvedVersionId = syncAndResolveScoreVersionId(responseData);
        const newPartialScoreId = getShelfPartialScoreIdForActiveCriterion(responseData);
        const newCommentId = newPartialScoreId
          ? getShelfOnCriteriaCommentId(responseData, newPartialScoreId)
          : undefined;
        if (newCommentId && newPartialScoreId) {
          return await updateComment(newCommentId, comment, null, null, null, null, null, newPartialScoreId)
            .then(
              (response: AxiosResponse<boolean>) => {
                if (response.data) {
                  openAlert('success');
                  if (setEditedItemId !== undefined) {
                    setEditedItemId('');
                  }
                  if (isCommentOnCriterion) {
                    return onSave({ scoreVersionId: resolvedVersionId });
                  } else showLoader(true);
                } else {
                  openAlert('error');
                }
              },
              () => {
                openAlert('error');
              },
            )
            .catch(() => {
              openAlert('error');
            })
            .finally(() => {
              setIsCommentUpdate(false);
              setIsCommentEdit(false);
            });
        }
        openAlert('error');
        setIsCommentUpdate(false);
        setIsCommentEdit(false);
      });
    } else {
      showLoader(true);
      updateComment(
        commentId,
        comment,
        null,
        null,
        null,
        null,
        null,
        activeCriterion ? activeCriterion.partialScoreId : null,
      )
        .then(
          (response: AxiosResponse<boolean>) => {
            if (response.data) {
              if (setEditedItemId !== undefined) {
                setEditedItemId('');
              }
              onSave({
                commentTypeId: isCommentOnCriterion ? CommentTypeId.ONCRITERIA : CommentTypeId.OFFPAGE,
              });
            } else {
              openAlert('error');
            }
          },
          () => {
            openAlert('error');
          },
        )
        .finally(() => {
          setIsCommentEdit(false);
        });
    }
  };

  const handleSave = () => {
    if (isEnabled) {
      setComment(comment);
      saveEdited();
    } else {
      handleAdd();
      setComment('');
    }
  };

  const handleCancel = () => {
    if (isEnabled) {
      setComment(commentProp);
      if (setEditedItemId) {
        setEditedItemId('');
      }
    } else {
      setComment('');
    }
  };

  const handleEdit = () => {
    if (setEditedItemId !== undefined) {
      setEditedItemId(commentId);
    }
  };

  const handleRemoveComment = () => {
    if (
      orgzId &&
      canEditScore &&
      scoreStateAssessed &&
      !examTaskGroupSolutionScoreVersionId &&
      isCommentOnCriterion &&
      (isUserKGZ || isUserKK)
    ) {
      showLoader(true);
      setIsCommentUpdate(true);
      setIsPromptOpen(false);
      openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
        const resolvedVersionId = syncAndResolveScoreVersionId(responseData);
        const newPartialScoreId = getShelfPartialScoreIdForActiveCriterion(responseData);
        const newCommentId = newPartialScoreId
          ? getShelfOnCriteriaCommentId(responseData, newPartialScoreId)
          : undefined;
        if (newCommentId && newPartialScoreId) {
          return await deleteComment(newCommentId, newPartialScoreId)
            .then(
              (response: AxiosResponse<boolean>) => {
                if (response.data) {
                  setExamTaskGroupSolutionScoreVersionId(undefined);
                  if (setEditedItemId !== undefined) {
                    setEditedItemId('');
                  }
                  return onSave({ scoreVersionId: resolvedVersionId, commentTypeId: CommentTypeId.ONCRITERIA });
                } else {
                  openAlert('error');
                }
              },
              () => {
                openAlert('error');
              },
            )
            .catch(() => {
              openAlert('error');
            })
            .finally(() => {
              setIsCommentEdit(false);
              setIsCommentUpdate(false);
            });
        }
        openAlert('error');
        setIsCommentEdit(false);
        setIsCommentUpdate(false);
      });
    } else {
      showLoader(true);
      deleteComment(commentId, activeCriterion ? activeCriterion.partialScoreId : null)
        .then(
          (response: AxiosResponse<boolean>) => {
            if (response.data) {
              if (setEditedItemId !== undefined) {
                setEditedItemId('');
              }
              openAlert('success');
              setIsPromptOpen(false);
              onSave({ commentTypeId: CommentTypeId.OFFPAGE });
            } else {
              openAlert('error');
              setIsPromptOpen(false);
            }
          },
          () => {
            openAlert('error');
            setIsPromptOpen(false);
          },
        )
        .finally(() => {
          setIsCommentEdit(false);
        });
    }
  };

  const getActionsMenuIcon = (): JSX.Element => {
    const menuItems: ActionMenuItem[] = [];
    menuItems.push({
      text: 'Edytuj',
      onClick: () => handleEdit(),
    });
    if (
      !isCommentOnCriterion ||
      canDeleteForcedComment ||
      ((isUserCZE || isUserCZWO) && !!examTaskGroupSolutionScoreVersionId)
    ) {
      menuItems.push({
        text: 'Usuń',
        onClick: () => setIsPromptOpen(true),
      });
    }
    return <ActionsMenu menuItems={menuItems} />;
  };

  const getCommentErrorMessage = () => {
    return isCommentToShort && isCommentOnCriterion && canAddCommentOnCriterion
      ? `Komentarz jest za krótki. Minimalna liczba znaków wynosi ${COMMENTS_MIN_LENGTH}.`
      : isCommentToLong
      ? `Komentarz jest za długi. Maksymalna liczba znaków wynosi ${COMMENTS_MAX_LENGTH}.`
      : '';
  };

  const isCommentError = () => {
    return (
      comment !== '' &&
      ((isCommentOnCriterion && canAddCommentOnCriterion && (isCommentToLong || isCommentToShort)) || isCommentToLong)
    );
  };

  const isCollapsedReadOnlyComment = existingComment && !hasCommentFocus && !isEnabled;

  return (
    <div
      className={!isEnabled ? classes.commentEditorContainer : classes.commentEditorContainerEdit}
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
    >
      {isCollapsedReadOnlyComment ? (
        <div
          tabIndex={0}
          role="button"
          className={classes.commentCollapsedText}
          title={comment}
          onFocus={() => setHasCommentFocus(true)}
          onClick={() => setHasCommentFocus(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setHasCommentFocus(true);
            }
          }}
        >
          {comment}
        </div>
      ) : (
        <TextField
          inputRef={inputRef}
          value={comment}
          multiline={!existingComment || hasCommentFocus || isEnabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleCommentChange(e.target.value)}
          variant={existingComment ? 'standard' : 'outlined'}
          fullWidth
          focused={isCommentOnCriterion && canAddCommentOnCriterion}
          placeholder={
            isCommentOnCriterion && canAddCommentOnCriterion
              ? 'Dodaj komentarz do ocenianego kryterium'
              : 'Dodaj komentarz...'
          }
          onFocus={() => setHasCommentFocus(true)}
          onBlur={() => setHasCommentFocus(false)}
          error={isCommentError()}
          helperText={
            comment &&
            (!existingComment || isEnabled) && (
              <span
                style={{
                  fontSize: '12px',
                  position: 'absolute',
                  bottom: isEnabled ? '-10px' : '2px',
                  color: isCommentError() ? '#BC0000' : undefined,
                }}
              >
                {`${comment.length}/${COMMENTS_MAX_LENGTH} ${isCommentError() ? getCommentErrorMessage() : ''}`}{' '}
              </span>
            )
          }
          inputProps={{
            'data-testid': 'comment-textarea',
            style: {
              maxHeight: '100px',
              overflow: 'hidden auto',
              paddingBottom: comment && !existingComment && isCommentOnCriterion ? '30px' : '',
            },
          }}
          InputProps={{
            endAdornment: existingComment ? (
              isEnabled && (
                <div style={{ display: 'flex' }}>
                  <ButtonOne
                    color="primary"
                    variant="outlined"
                    onClick={handleCancel}
                    style={{ margin: '5px' }}
                    aria-label="przycisk anuluj"
                  >
                    Anuluj
                  </ButtonOne>
                  <ButtonOne
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    style={{ margin: '5px' }}
                    disabled={commentProp === comment || isCommentToLong || (isCommentOnCriterion && isCommentToShort)}
                    aria-label="przycisk zapisz"
                  >
                    Zapisz
                  </ButtonOne>
                </div>
              )
            ) : (
              <InputAdornment position="end">
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ alignSelf: 'flex-end', paddingRight: '8px' }}>
                    {comment && (
                      <TooltipOne title="Anuluj wprowadzanie komentarza">
                        <IconButton disableRipple onClick={handleCancel} className={classes.xIcon}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </TooltipOne>
                    )}
                  </div>
                  <div style={{ position: 'relative' }}>
                    {!canAddComment && <CancelIcon className={classes.disabledAction} />}
                    <TooltipOne
                      title={
                        isCommentError() || !canAddComment
                          ? 'Zatwierdzenie komentarza nie jest w tej chwili możliwe'
                          : 'Zatwierdź komentarz'
                      }
                    >
                      <IconButton
                        disableRipple
                        onClick={handleSave}
                        disabled={isCommentError() || !canAddComment || !comment.length}
                        className={classes.arrowIcon}
                        aria-label="przycisk zapisz"
                      >
                        <ArrowCircleRightRoundedIcon fontSize="large" />
                      </IconButton>
                    </TooltipOne>
                  </div>
                </div>
              </InputAdornment>
            ),
            readOnly: (existingComment && !isEnabled) || (!isEnabled && !canAddComment) ? true : false,
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          color: isCommentToLong ? '#BC0000' : undefined,
        }}
      >
        {existingComment && isActionVisible && <div className={classes.actionIcon}>{getActionsMenuIcon()}</div>}
      </div>
      {isPromptOpen && (
        <Prompt
          type="confirmation"
          mainActionButtonText="TAK"
          open={true}
          mainText="Czy na pewno chcesz usunąć komentarz?"
          onConfirm={handleRemoveComment}
          onCancel={() => setIsPromptOpen(false)}
        />
      )}
    </div>
  );
};
